import { Link, router } from "@inertiajs/react"
import { useCallback, useEffect, useRef, useState } from "react"
import {
  DEFAULT_GENERATION_MODEL_EXTRA_SETTINGS,
  GenerationModelOverridesFields,
  applyGenerationModelOverride,
  type GenerationModelExtraSettings,
  type GenerationModelOverrideContext,
} from "@/components/workspace/generation-model-overrides-fields"
import { useTranslation } from "@/hooks/use-translation"
import type { GenerationModelsByContext, ProjectSummary } from "@/types/workspace"

type FormProject = GenerationModelExtraSettings & {
  generation_model_overrides: Record<string, string>
}

type Props = {
  project: ProjectSummary
  generationModelsByContext?: GenerationModelsByContext
  onSaved?: () => void
}

function initialForm(p: ProjectSummary): FormProject {
  return {
    generation_model_overrides: { ...(p.generation_model_overrides ?? {}) },
    openrouter_video_resolution:
      p.openrouter_video_resolution ?? DEFAULT_GENERATION_MODEL_EXTRA_SETTINGS.openrouter_video_resolution,
    happyhorse_video_resolution:
      p.happyhorse_video_resolution ?? DEFAULT_GENERATION_MODEL_EXTRA_SETTINGS.happyhorse_video_resolution,
    byteplus_seedance_video_resolution:
      p.byteplus_seedance_video_resolution ??
      DEFAULT_GENERATION_MODEL_EXTRA_SETTINGS.byteplus_seedance_video_resolution,
    byteplus_seedance_generate_audio:
      p.byteplus_seedance_generate_audio ?? DEFAULT_GENERATION_MODEL_EXTRA_SETTINGS.byteplus_seedance_generate_audio,
    byteplus_seedance_draft_default:
      p.byteplus_seedance_draft_default ?? DEFAULT_GENERATION_MODEL_EXTRA_SETTINGS.byteplus_seedance_draft_default,
    kling_generate_audio: p.kling_generate_audio ?? DEFAULT_GENERATION_MODEL_EXTRA_SETTINGS.kling_generate_audio,
    location_variant_kling_image_fidelity:
      p.location_variant_kling_image_fidelity == null
        ? DEFAULT_GENERATION_MODEL_EXTRA_SETTINGS.location_variant_kling_image_fidelity
        : String(p.location_variant_kling_image_fidelity),
    character_outfit_kling_image_fidelity:
      p.character_outfit_kling_image_fidelity == null
        ? DEFAULT_GENERATION_MODEL_EXTRA_SETTINGS.character_outfit_kling_image_fidelity
        : String(p.character_outfit_kling_image_fidelity),
    seedance_animatic_reference_block:
      p.seedance_animatic_reference_block ?? DEFAULT_GENERATION_MODEL_EXTRA_SETTINGS.seedance_animatic_reference_block,
    generation_image_sizes: { ...(p.generation_image_sizes ?? {}) },
  }
}

export function WorkspaceProvidersForm({ project: p, generationModelsByContext = {}, onSaved }: Props) {
  const { t } = useTranslation(["workspace", "common"])
  const [data, setData] = useState<FormProject>(() => initialForm(p))
  const [saving, setSaving] = useState(false)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dataRef = useRef(data)
  dataRef.current = data

  const scheduleSave = useCallback(
    (next: FormProject) => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
      saveTimerRef.current = setTimeout(() => {
        setSaving(true)
        router.patch(
          `/projects/${p.id}`,
          { project: next },
          {
            preserveScroll: true,
            onFinish: () => setSaving(false),
            onSuccess: () => onSaved?.(),
          },
        )
      }, 400)
    },
    [onSaved, p.id],
  )

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    }
  }, [])

  const patchProject = useCallback(
    (partial: Partial<FormProject>) => {
      const next = { ...dataRef.current, ...partial }
      dataRef.current = next
      setData(next)
      scheduleSave(next)
    },
    [scheduleSave],
  )

  const setOverride = useCallback(
    (context: GenerationModelOverrideContext, modelKey: string) => {
      patchProject({
        generation_model_overrides: applyGenerationModelOverride(
          dataRef.current.generation_model_overrides,
          context,
          modelKey,
        ),
      })
    },
    [patchProject],
  )

  return (
    <div className="space-y-4">
      <p className="text-xs text-ui-text-muted">
        {t("providers.intro")}{" "}
        <Link
          href="/studio/platform-config"
          className="text-ui-accent-text underline decoration-ui-accent/40 hover:text-ui-accent-text"
        >
          {t("providers.studioAi")}
        </Link>
        .
      </p>

      <GenerationModelOverridesFields
        generationModelsByContext={generationModelsByContext}
        overrides={data.generation_model_overrides}
        effectiveModelKeys={p.effective_generation_model_keys}
        extraSettings={data}
        onOverrideChange={setOverride}
        onExtraSettingsChange={patchProject}
      />

      {saving ? <p className="text-[11px] text-ui-text-secondary">{t("common:saving")}</p> : null}
    </div>
  )
}
